import { useNavigate, useParams } from "react-router-dom";
import { ServiceRequestsIcon } from "../../../../../assets/icons";
import Button from "../../../../../common/components/xepp/button/Button";
import styles from "./NewWorkflowPage.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { RootState } from "../../../../../stores/store";
import { toast } from "react-toastify";
import { setWorkflowDescription, setWorkflowId, setWorkflowName } from "../../../../../stores/slices/workflow-slice";
import {
	useCreateWorkflowMutation,
	useEditWorkflowBasicInfoMutation,
	useLazyGetServiceRequestsQuery,
	useLazyGetWorkflowByIdQuery,
} from "../../../../../service/customersupport-service";
import Select, { SelectItem } from "../../../../../common/components/new/select/Select";
import { hideLoader, showLoader } from "../../../../../stores/slices/loader-slice";
import { ApplicationRoutes } from "../../../../../routes";
import { getErrorMessage } from "../../../../../utils/error-handler";

const NewWorkflowPage = () => {
	let { serviceRequestId = "", workflowId = "" } = useParams();
	const isEditMode = !!workflowId;
	const currentState = useSelector((state: RootState) => state.workflow);
	const [createWorkflow, { data: workflowResponse, isLoading: isCreatingWorkflow }] = useCreateWorkflowMutation();
	const [editWorkflow, { isLoading: isEditingWorkflow }] = useEditWorkflowBasicInfoMutation();
	const [getWorkflowById, { data: workflowData }] = useLazyGetWorkflowByIdQuery();
	const dispatch = useDispatch();
	const navigator = useNavigate();

	const [getServiceRequests, { data: serviceRequests, isLoading: isLoadingNew, isFetching: isFetchingNew, isSuccess }] = useLazyGetServiceRequestsQuery();

	useEffect(() => {
		getServiceRequests({ page: 1, pageSize: 50 });
		if (isEditMode) {
			getWorkflowById(workflowId);
		}
	}, [getServiceRequests, isEditMode, workflowId, getWorkflowById]);

	const [workflow, setworkflow] = useState({
		workflowName: isEditMode ? "" : currentState.name,
		workflowDescription: isEditMode ? "" : currentState.description,
		serviceRequest: new SelectItem(serviceRequestId, ""),
	});

	// Load workflow data when in edit mode
	useEffect(() => {
		if (isEditMode && workflowData) {
			setworkflow({
				workflowName: workflowData.name,
				workflowDescription: workflowData.description,
				serviceRequest: new SelectItem(workflowData.serviceRequest?.id ?? "", workflowData.serviceRequest?.name ?? ""),
			});
		}
	}, [workflowData, isEditMode]);

	const handleChange = (e: any) => {
		const { name, value } = e.target;
		setworkflow(prev => ({ ...prev, [name]: value }));
	};

	const isFormValid = () => {
		const { workflowName, workflowDescription, serviceRequest } = workflow;

		// Check if workflow name is not empty
		if (!workflowName || workflowName.trim() === "") {
			return false;
		}

		// Check if description is not empty
		if (!workflowDescription || workflowDescription.trim() === "") {
			return false;
		}

		// Check if service request is selected (only in create mode)
		if (!isEditMode && (!serviceRequest.key || serviceRequest.key === "")) {
			return false;
		}

		return true;
	};

	const onContinueClick = async () => {
		const { workflowName, workflowDescription, serviceRequest } = workflow;

		// Validate all required fields
		if (!workflowName || workflowName.trim() === "") {
			toast.warning("Workflow Name is required");
			return;
		}

		if (!workflowDescription || workflowDescription.trim() === "") {
			toast.warning("Description is required");
			return;
		}

		if (!isEditMode && (!serviceRequest.key || serviceRequest.key === "")) {
			toast.warning("Service Request is required");
			return;
		}

		dispatch(setWorkflowName(workflowName));
		dispatch(setWorkflowDescription(workflowDescription));

		dispatch(showLoader());
		try {
			if (isEditMode) {
				await editWorkflow({
					workflowId: workflowId,
					name: workflow.workflowName,
					description: workflow.workflowDescription,
				}).unwrap();
				toast.success("Workflow updated successfully");
				navigator(-1);
			} else {
				await createWorkflow({
					name: workflow.workflowName,
					description: workflow.workflowDescription,
					serviceRequestId: workflow.serviceRequest.key,
				}).unwrap();
			}
		} catch (error: any) {
			toast.error(getErrorMessage(error.data) ?? `There was an error ${isEditMode ? "updating" : "creating"} a workflow`);
		}
		dispatch(hideLoader());
	};

	useEffect(() => {
		console.log(workflowResponse);
		if (workflowResponse?.workflowId != null) {
			dispatch(setWorkflowId(workflowResponse!.workflowId));
			navigator(`../${ApplicationRoutes.WORKFLOWS_MANAGMENT}/${workflowResponse!.workflowId}/${ApplicationRoutes.CUSTOM_STEPS}`);
		}
	}, [workflowResponse, isSuccess]);

	const onCancelClick = () => {
		dispatch(setWorkflowName(""));
		dispatch(setWorkflowDescription(""));
		navigator(-1);
	};

	const getSelectItems = (): SelectItem[] => {
		return serviceRequests?.items?.map(x => new SelectItem(x.id, x.name)) ?? [];
	};

	return (
		<>
			<div className={styles.headerWrapper}>
				<div className={styles.iconContainer}>{<ServiceRequestsIcon />}</div>
				<h3>{isEditMode ? "Update Workflow" : "Add New Workflow"}</h3>
			</div>
			<div className={styles.bodyWrapper}>
				<div className={styles.bodyContainer}>
					<div className={styles.content}>
						{!isEditMode && (
							<div className={styles.workflowServiceRequestRow}>
								<div className={styles.workflowServiceRequestContainer}>
									<span>
										Service Request <span style={{ color: "red" }}>*</span>
									</span>
								</div>
								<div className={styles.workflowServiceRequestValueContainer}>
									<Select
										label="Select Service Request"
										placeholder=""
										items={getSelectItems()}
										selected={workflow.serviceRequest}
										onChange={(item: SelectItem | undefined) => {
											if (item?.key !== undefined) {
												setworkflow({ ...workflow, serviceRequest: item });
											}
										}}
										searchable={true}
									/>
								</div>
							</div>
						)}
						{isEditMode && workflow.serviceRequest.label && (
							<div className={styles.workflowServiceRequestRow}>
								<div className={styles.workflowServiceRequestContainer}>
									<span>Service Request</span>
								</div>
								<div className={styles.workflowServiceRequestValueContainer}>
									<div className={styles.readOnlyField}>
										<span>{workflow.serviceRequest.label}</span>
									</div>
								</div>
							</div>
						)}
						<div className={styles.workflowNameRow}>
							<div className={styles.workflowNameContainer}>
								<span>
									Workflow Name <span style={{ color: "red" }}>*</span>
								</span>
							</div>
							<div className={styles.workflowNameValueContainer}>
								<div className={styles.workflowNameInputContainer}>
									<input type="text" name="workflowName" value={workflow.workflowName} onChange={handleChange} placeholder="Enter Workflow Name" />
								</div>
							</div>
						</div>
						<div className={styles.descriptionRow}>
							<div className={styles.descriptionContainer}>
								<span>
									Description <span style={{ color: "red" }}>*</span>
								</span>
							</div>
							<div className={styles.descriptionValueContainer}>
								<div className={styles.descriptionInputContainer}>
									<textarea
										name="workflowDescription"
										value={workflow.workflowDescription}
										onChange={handleChange}
										placeholder="Please provide a short description of the workflow you're creating, including relevant details (e.g., timeline, or special requirements)"
									/>
								</div>
							</div>
						</div>
					</div>
					<div className={styles.footer}>
						<Button text={isEditMode ? "Cancel" : "Cancel Setup"} onClick={onCancelClick} variant={"outline"} />
						<Button text={isEditMode ? "Update Workflow" : "Continue Setup"} onClick={onContinueClick} variant={"secondary"} disabled={!isFormValid()} />
					</div>
				</div>
			</div>
		</>
	);
};

export default NewWorkflowPage;
